/**
 * V7-B — „Experten-Radar": Werte, über die deine YouTube-Experten reden, die
 * aber (noch) NICHT in deiner Watchlist sind. Reine Lese-/Verdichtungs-Schicht
 * über v5_expert_opinions — kostet keine zusätzlichen Marktdaten (die entstehen
 * erst, wenn du einen Wert wirklich aufnimmst).
 */
import { supabase } from '@/lib/supabase';
import {
  summarizeExpertOpinions,
  type ExpertConsensus,
  type ExpertOpinionInput,
} from '@/lib/expert-consensus';

export interface ExpertRadarEntry {
  symbol: string;
  consensus: ExpertConsensus;
}

export interface ExpertFreshness {
  /** Zeitpunkt des zuletzt geprüften Kanals (ISO) oder null. */
  lastCheckedAt: string | null;
  /** Neueste Video-Veröffentlichung, aus der eine Meinung entstand (ISO). */
  latestVideoAt: string | null;
  /** Anzahl Meinungen aus Videos der letzten 24 h. */
  neueLetzte24h: number;
}

/**
 * Frische-Signal für die Experten-Karten (#43-Nachfrage: „steht das nicht
 * schon länger drin?"). Zeigt sichtbar, dass die Aufnahme läuft — getrennt von
 * der (real stabilen) Konsens-Zahl je Wert.
 */
export async function fetchExpertFreshness(): Promise<ExpertFreshness> {
  const since = new Date(Date.now() - 86_400_000).toISOString(); // letzte 24 h
  const [checkRes, latestRes, countRes] = await Promise.all([
    supabase
      .from('v5_expert_channels')
      .select('last_checked_at')
      .not('last_checked_at', 'is', null)
      .order('last_checked_at', { ascending: false })
      .limit(1),
    supabase
      .from('v5_expert_opinions')
      .select('video_published_at')
      .not('video_published_at', 'is', null)
      .order('video_published_at', { ascending: false })
      .limit(1),
    supabase
      .from('v5_expert_opinions')
      .select('id', { count: 'exact', head: true })
      .gte('video_published_at', since),
  ]);
  return {
    lastCheckedAt:
      (checkRes.data?.[0] as { last_checked_at?: string } | undefined)?.last_checked_at ?? null,
    latestVideoAt:
      (latestRes.data?.[0] as { video_published_at?: string } | undefined)?.video_published_at ??
      null,
    neueLetzte24h: countRes.count ?? 0,
  };
}

interface OpinionRow {
  symbol: string;
  channel_id: string;
  stance: 'bullish' | 'neutral' | 'bearish';
  level_note: string | null;
  summary: string | null;
  video_url: string | null;
  video_published_at: string | null;
}

export async function fetchExpertRadar(maxAgeDays = 14): Promise<ExpertRadarEntry[]> {
  const cutoff = new Date(Date.now() - maxAgeDays * 86_400_000).toISOString();
  const [opinionsRes, channelsRes, wlRes] = await Promise.all([
    supabase
      .from('v5_expert_opinions')
      .select('symbol, channel_id, stance, level_note, summary, video_url, video_published_at')
      .gte('video_published_at', cutoff),
    supabase.from('v5_expert_channels').select('channel_id, display_name, angle'),
    supabase.from('v5_watchlist_entries').select('symbol').eq('is_archived', false),
  ]);
  if (opinionsRes.error) throw new Error(`v5_expert_opinions: ${opinionsRes.error.message}`);

  const channels = new Map<string, { display_name: string; angle: string | null }>();
  for (const c of (channelsRes.data ?? []) as Array<{
    channel_id: string;
    display_name: string;
    angle: string | null;
  }>) {
    channels.set(c.channel_id, { display_name: c.display_name, angle: c.angle });
  }
  const watchlist = new Set(
    ((wlRes.data ?? []) as Array<{ symbol: string }>).map((r) => r.symbol.toUpperCase())
  );

  const bySymbol = new Map<string, ExpertOpinionInput[]>();
  for (const r of (opinionsRes.data ?? []) as OpinionRow[]) {
    const sym = r.symbol.toUpperCase();
    if (watchlist.has(sym)) continue; // nur was NICHT bei dir ist
    const ch = channels.get(r.channel_id);
    const arr = bySymbol.get(sym) ?? [];
    arr.push({
      channelName: ch?.display_name ?? 'Experte',
      angle: ch?.angle ?? null,
      stance: r.stance,
      levelNote: r.level_note,
      summary: r.summary,
      videoPublishedAt: r.video_published_at ?? new Date().toISOString(),
      videoUrl: r.video_url,
    });
    bySymbol.set(sym, arr);
  }

  return [...bySymbol.entries()]
    .map(([symbol, ops]) => ({ symbol, consensus: summarizeExpertOpinions(ops, { maxAgeDays }) }))
    .filter((e) => e.consensus.tone !== 'keine')
    .sort(
      (a, b) =>
        b.consensus.experten - a.consensus.experten || b.consensus.bullish - a.consensus.bullish
    )
    .slice(0, 10);
}
